import { useState } from "react"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useMutation } from "convex/react"
import { api } from "../../convex/_generated/api"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Logo } from "@/components/logo"
import { useAuthGate } from "@/hooks/use-auth-gate"
import { errorMessage } from "@/lib/errors"

export const Route = createFileRoute("/recruit_/invite")({
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: RecruitInvitePage,
})

function RecruitInvitePage() {
  const { token } = Route.useSearch()
  const gate = useAuthGate()
  const acceptInvite = useMutation(api.companyMembers.acceptInvite)
  const navigate = useNavigate()
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const validToken = token && /^[A-Za-z0-9_-]{32,128}$/.test(token)
  const returnTo = validToken ? `/recruit/invite?token=${token}` : "/recruit/invite"

  if (!validToken) {
    return <InviteShell title="This invitation link is invalid" detail="Ask your company owner to send a new invitation." />
  }

  if (gate.status === "loading") {
    return (
      <div className="mkt-auth home-wf">
        <Skeleton className="h-40 w-full max-w-sm" />
      </div>
    )
  }

  if (gate.status === "anonymous" || gate.status === "no-profile") {
    return (
      <InviteShell
        title="Join your company"
        detail="Create or sign in to an employer account using the exact email address that received this invitation."
        actions={
          <>
            <Button asChild>
              <Link to="/recruit/join" search={{ returnTo }}>Create employer account</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/login" search={{ returnTo }}>Log in</Link>
            </Button>
          </>
        }
      />
    )
  }

  if (gate.profile.role !== "employer") {
    return <InviteShell title="Employer account required" detail="This invitation can only be accepted by an employer account with the invited email address." />
  }

  const accept = async () => {
    setPending(true)
    setError(null)
    try {
      await acceptInvite({ token })
      await navigate({ to: "/recruit" })
    } catch (reason) {
      setError(errorMessage(reason, "Could not accept this invitation"))
    } finally {
      setPending(false)
    }
  }

  return (
    <InviteShell
      title="Accept company invitation"
      detail="You are about to join the company that invited this email address."
      actions={
        <Button type="button" onClick={accept} disabled={pending}>
          {pending ? "Accepting…" : "Accept invitation"}
        </Button>
      }
      error={error}
    />
  )
}

function InviteShell({
  title,
  detail,
  actions,
  error,
}: {
  title: string
  detail: string
  actions?: React.ReactNode
  error?: string | null
}) {
  return (
    <div className="mkt-auth home-wf">
      <Link to="/" className="home-wf-brand" aria-label="Hire home">
        <Logo />
      </Link>
      <div className="mkt-auth-card">
        <div className="mkt-auth-panel space-y-5">
          <header className="mkt-auth-head">
            <h1 className="mkt-auth-title">{title}</h1>
            <p className="mkt-auth-desc">{detail}</p>
          </header>
          {error ? <p className="rounded-md border border-border bg-muted p-3 text-sm text-foreground">{error}</p> : null}
          {actions ? <div className="flex flex-wrap gap-3">{actions}</div> : null}
        </div>
      </div>
    </div>
  )
}
